import { C, Note, P, Terms } from '../prose';
import type { Topic } from '../types';

/** Getting a site from "added" to "actually enforcing something". */
export const einrichtung = {
  einstieg: {
    group: 'einrichtung',
    title: { de: 'Wie das Ganze zusammenhängt', en: 'How the pieces fit together' },
    teaser: {
      de: 'Was das Snippet, die Regeln und die Veröffentlichung miteinander zu tun haben.',
      en: 'What the snippet, the rules and publishing have to do with each other.',
    },
    body: {
      de: (
        <>
          <P>
            Auf Ihrer Seite steht ein einziges Skript, der Loader. Es läuft, bevor irgendetwas
            anderes läuft, und hält alles an, was Daten an Dritte schicken würde — Skripte,
            Bilder, iframes, Cookies. Erst wenn eine Einwilligung vorliegt, gibt es genau das
            frei, was erlaubt wurde.
          </P>
          <P>
            Was angehalten und was freigegeben wird, steht in der veröffentlichten Fassung
            Ihrer Konfiguration. Sie wird als Datei ausgeliefert und im Browser gelesen; im
            Editor sehen Sie einen Entwurf davon. Zwischen Entwurf und veröffentlichter
            Fassung liegt ein bewusster Schritt, denn erst der ändert, was Besucher sehen.
          </P>
          <Terms
            items={[
              ['Banner', 'Was gefragt wird, und wie es aussieht.'],
              ['Blockieren & Anbieter', 'Was angehalten wird, und wer als Empfänger genannt wird.'],
              ['Auswertung', 'Wie oft entschieden wurde und wie.'],
              ['Nachweise', 'Der Beleg für eine einzelne Entscheidung.'],
            ]}
          />
          <Note>
            Solange nichts veröffentlicht ist, findet der Loader keine Konfiguration und
            arbeitet mit seinen eingebauten Notfall-Regeln: er blockiert, fragt aber nichts.
            Das ist sicher und unbrauchbar — die erste Veröffentlichung ist der Schritt, der
            das Produkt einschaltet.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            One script sits on your site: the loader. It runs before anything else does and
            holds back everything that would send data to a third party — scripts, images,
            iframes, cookies. Once consent exists, it releases exactly what was allowed.
          </P>
          <P>
            What gets held and what gets released is written in the published version of your
            configuration. It is delivered as a file and read in the browser; the editor shows
            you a draft of it. Between the draft and the published version sits one deliberate
            step, because that step is what changes what visitors see.
          </P>
          <Terms
            items={[
              ['Banner', 'What gets asked, and how it looks.'],
              ['Blocking & vendors', 'What gets held back, and who is named as a recipient.'],
              ['Analytics', 'How often people decided, and how.'],
              ['Proofs', 'The evidence for one individual decision.'],
            ]}
          />
          <Note>
            Until you publish, the loader finds no configuration and falls back to its
            built-in emergency rules: it blocks, but asks nothing. That is safe and useless —
            the first publish is the step that switches the product on.
          </Note>
        </>
      ),
    },
  },

  'website-anlegen': {
    group: 'einrichtung',
    title: { de: 'Eine Website anlegen', en: 'Adding a website' },
    teaser: {
      de: 'Was der Assistent in fünf Schritten festlegt und warum er die Website sofort erzeugt.',
      en: 'What the five-step wizard settles, and why it creates the site immediately.',
    },
    body: {
      de: (
        <>
          <P>
            Eine Website ist eine Domain mit einem eigenen Banner, eigenen Regeln und eigener
            Auswertung. Zwei Marken auf zwei Domains sind zwei Websites, auch wenn dieselbe
            Firma dahintersteht — Nachweise und Statistik werden pro Website geführt.
          </P>
          <P>
            Der Assistent legt die Website schon am Ende des ersten Schritts an, nicht erst am
            Ende. Wer in der Mitte abbricht, hat deshalb eine echte Website und keine verlorene
            Eingabe; die Einrichtungs-Checkliste auf der Übersicht macht genau dort weiter.
          </P>
          <Terms
            items={[
              ['Domain', 'Nur der Host — ohne https:// und ohne Pfad.'],
              ['Name', 'Nur für Sie. Er taucht auf der Website nirgends auf.'],
              ['Site-Schlüssel', 'Steht im Snippet und verbindet Seite und Konfiguration. Er ist nicht geheim.'],
            ]}
          />
        </>
      ),
      en: (
        <>
          <P>
            A website is one domain with its own banner, its own rules and its own analytics.
            Two brands on two domains are two websites, even behind one company — proofs and
            statistics are kept per website.
          </P>
          <P>
            The wizard creates the site at the end of step one, not at the end of step five.
            Leaving halfway therefore leaves a real site rather than a lost form, and the setup
            checklist on the site's page picks up exactly where you stopped.
          </P>
          <Terms
            items={[
              ['Domain', 'The host only — no https://, no path.'],
              ['Name', 'For you alone. It appears nowhere on the website.'],
              ['Site key', 'Sits in the snippet and ties the page to its configuration. It is not a secret.'],
            ]}
          />
        </>
      ),
    },
  },

  domains: {
    group: 'einrichtung',
    title: { de: 'Domains und Testumgebungen', en: 'Domains and test environments' },
    teaser: {
      de: 'Welche Hosts das Banner laden dürfen — und wie Staging die Zahlen nicht verdirbt.',
      en: 'Which hosts may load the banner — and how staging stays out of your numbers.',
    },
    body: {
      de: (
        <>
          <P>
            Nur eingetragene Hosts dürfen das Banner laden und Ereignisse melden. Alles andere
            wird abgewiesen. Das ist kein Komfort-Filter, sondern der Grund, warum ein fremder
            Einbau Ihres Site-Schlüssels Ihre Nachweise nicht verunreinigen kann.
          </P>
          <P>
            Eine als <strong>Testumgebung</strong> markierte Domain zeigt das Banner mit einem
            Hinweis und meldet nichts. Der übliche Rat der Branche lautet, Staging hinterher aus
            den Berichten zu filtern — das setzt voraus, dass jemand den Filter jedes Mal
            erinnert. Ein Kennzeichen an der Domain erinnert sich selbst.
          </P>
          <Note>
            Die Hauptdomain lässt sich nicht entfernen. Wird eine andere Domain entfernt, lädt
            das Banner dort ab sofort nicht mehr — und die Seite läuft ohne Einwilligungsabfrage
            weiter, wenn dort Anbieter eingebunden sind.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            Only listed hosts may load the banner and report events; everything else is
            rejected. This is not a convenience filter — it is the reason someone else pasting
            your site key into their page cannot pollute your proofs.
          </P>
          <P>
            A domain marked as a <strong>test environment</strong> shows the banner with a
            notice and reports nothing. The industry's usual advice is to filter staging out of
            reports afterwards, which assumes somebody remembers the filter every time. A flag
            on the domain remembers itself.
          </P>
          <Note>
            The primary domain cannot be removed. Remove any other domain and the banner stops
            loading there immediately — leaving the page running without a consent prompt if
            vendors are embedded on it.
          </Note>
        </>
      ),
    },
  },

  snippet: {
    group: 'einrichtung',
    title: { de: 'Das Snippet einbauen', en: 'Installing the snippet' },
    teaser: {
      de: 'Warum die Position im <head> über Wirkung oder Wirkungslosigkeit entscheidet.',
      en: 'Why its position in the <head> decides whether any of this works.',
    },
    body: {
      de: (
        <>
          <P>
            Das Snippet gehört so weit oben in den <C>&lt;head&gt;</C> wie möglich. Alles, was
            davor steht, ist bereits gelaufen, bevor der Loader existiert — und genau das wäre
            der Verstoß, den er verhindern soll.
          </P>
          <P>
            Der häufigste Fehler ist kein fehlendes Snippet, sondern ein zu spätes: unter einem
            Tag-Manager, unter einem Consent-Skript des Baukastens, oder von einem
            Caching-Plugin ans Seitenende verschoben. Das Banner erscheint dann, sieht richtig
            aus, und hat trotzdem nichts angehalten.
          </P>
          <P>
            <strong>Jetzt prüfen</strong> lädt Ihre Startseite und sieht nach: ist die Seite
            erreichbar, steht der Loader drin, gehört er zu dieser Website, und hat vor der
            Einwilligung etwas gefeuert. Der letzte Punkt ist der wichtige — er ist der
            Unterschied zwischen einer konformen Seite und einer, die konform aussieht.
          </P>
          <Note>
            Der Test prüft die Startseite. Kampagnen-Landingpages und Shop-Seiten mit eigenen
            Tags werden davon nicht erfasst — dafür ist der Scan da.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            The snippet belongs as high in the <C>&lt;head&gt;</C> as you can put it. Anything
            above it has already run before the loader exists — and that is precisely the
            violation the loader is there to prevent.
          </P>
          <P>
            The most common failure is not a missing snippet but a late one: below a tag
            manager, below the site builder's own consent script, or moved to the end of the
            page by a caching plugin. The banner then appears, looks correct, and has held back
            nothing.
          </P>
          <P>
            <strong>Check now</strong> loads your home page and looks: is the page reachable, is
            the loader there, does it belong to this website, and did anything fire before
            consent. The last one matters most — it is the difference between a compliant site
            and one that looks compliant.
          </P>
          <Note>
            The check reads the home page. Campaign landing pages and shop pages carrying their
            own tags are not covered by it — that is what the scan is for.
          </Note>
        </>
      ),
    },
  },

  rechtslage: {
    group: 'einrichtung',
    title: { de: 'Rechtslage und Regime', en: 'Legal regime' },
    teaser: {
      de: 'Woher das Regime kommt, was es erzwingt, und was bei unbekanntem Land gilt.',
      en: 'Where the regime comes from, what it enforces, and what happens when the country is unknown.',
    },
    body: {
      de: (
        <>
          <P>
            Das Regime wird pro Besucher bestimmt, nicht pro Website: dieselbe Seite fragt in
            Deutschland anders als in Kalifornien. Was hier steht, ist die Lage für Ihre
            Hauptdomain — die Zeile rechts nennt, woraus sie ermittelt wurde.
          </P>
          <Terms
            items={[
              ['Grundlage', '„Einwilligung vorab“ heißt: nichts läuft, bevor zugestimmt wurde. „Widerspruch möglich“ erlaubt den Start und einen Ausstieg.'],
              ['Ablehnen-Button', 'Wo er verpflichtend ist, lässt er sich im Editor nicht abschalten — der Versuch wird beim Veröffentlichen abgewiesen.'],
              ['Einwilligung gilt', 'Danach wird erneut gefragt. Eine inhaltliche Textänderung fragt schon vorher erneut.'],
            ]}
          />
          <Note>
            Ist das Land unbekannt, gilt das strengste Regime — nicht das mildeste. Das ist
            fest so, nicht eingestellt: Wer nicht zugeordnet werden kann, wird behandelt, als
            stünde er unter der DSGVO.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            The regime is resolved per visitor, not per website: the same page asks differently
            in Germany than in California. What you see here is the situation for your primary
            domain — the line on the right names what it was derived from.
          </P>
          <Terms
            items={[
              ['Legal basis', '"Consent first" means nothing runs before agreement. "Opt-out" permits the start and an exit.'],
              ['Reject button', 'Where it is mandatory the editor cannot switch it off — the attempt is refused at publish time.'],
              ['Consent lifetime', 'After this, visitors are asked again. A material change to the wording asks again sooner.'],
            ]}
          />
          <Note>
            When the country is unknown, the strictest regime applies — not the mildest. That
            is fixed, not configured: anyone who cannot be placed is treated as if they were
            covered by the GDPR.
          </Note>
        </>
      ),
    },
  },
} as const satisfies Record<string, Topic>;
